import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

interface PhysicianUploadHelpDialogProps {
  open: boolean;
  onClose: () => void;
}

export const PhysicianUploadHelpDialog: React.FC<PhysicianUploadHelpDialogProps> = ({
  open,
  onClose
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2 
      }}>
        <Typography variant="h6">
          Physician Upload File Specification
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* File Format Requirements */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              File Format Requirements
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a CSV file containing physician information. The file must include specific headers and follow the data format requirements below.
            </Alert>
          </Box>

          {/* Required Headers */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              CSV Headers (Required)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your CSV file must contain the following column headers in any order:
            </Typography>
            <Table size="small" sx={{ border: 1, borderColor: 'divider' }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Header Name</strong></TableCell>
                  <TableCell><strong>Required</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Example</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><code>CenterId</code></TableCell>
                  <TableCell><Chip label="Required" color="error" size="small" /></TableCell>
                  <TableCell>Center identifier (without company prefix)</TableCell>
                  <TableCell>H01, H02, ASC01</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Code</code></TableCell>
                  <TableCell><Chip label="Required" color="error" size="small" /></TableCell>
                  <TableCell>Unique physician code/identifier</TableCell>
                  <TableCell>DR001, SMITH123, PHY-045</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>FirstName</code></TableCell>
                  <TableCell><Chip label="Optional*" color="warning" size="small" /></TableCell>
                  <TableCell>Physician's first name</TableCell>
                  <TableCell>John, Mary, Robert</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>LastName</code></TableCell>
                  <TableCell><Chip label="Optional*" color="warning" size="small" /></TableCell>
                  <TableCell>Physician's last name</TableCell>
                  <TableCell>Smith, Johnson, Williams</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>MiddleName</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Physician's middle name or initial</TableCell>
                  <TableCell>A, Allen, Marie</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Specialty</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Medical specialty code (see valid values below)</TableCell>
                  <TableCell>CAR, ORTHO, GENSURG</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Npi</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>National Provider Identifier (any format)</TableCell>
                  <TableCell>1234567890, NPI123456</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Phone</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Contact phone number</TableCell>
                  <TableCell>(555) 123-4567, 555-123-4567</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Company</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Company identifier (will use current if not provided)</TableCell>
                  <TableCell>PSN, PARSEC</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              * Either FirstName or LastName must be provided (at least one is required)
            </Typography>
          </Box>

          {/* Valid Specialty Values */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Valid Specialty Values
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If providing a Specialty, it must match one of these exact values (case-sensitive):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                'UNK', 'CAR', 'ENT', 'FAM', 'GAS', 'GEN', 'GENSURG', 'INT', 
                'NEURO', 'GYN', 'ONC', 'ORTHO', 'PAIN', 'PLAS', 'POD', 'SPN', 'VAR'
              ].map((specialty) => (
                <Chip key={specialty} label={specialty} variant="outlined" size="small" />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Note: These are the code values. For reference: UNK=Unknown, CAR=Cardio, ENT=ENT, FAM=Family Med, GAS=Gastroenterology, GEN=General, GENSURG=General Surgery, INT=Internal Med, NEURO=Neurosurgery, GYN=OBGYN, ONC=Oncology, ORTHO=Ortho, PAIN=Pain, PLAS=Plastics, POD=Podiatry, SPN=Spine, VAR=Various Other
            </Typography>
          </Box>

          {/* Example CSV */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Example CSV Content
            </Typography>
            <Box sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              overflow: 'auto'
            }}>
              <pre style={{ margin: 0 }}>
{`CenterId,Code,FirstName,LastName,MiddleName,Specialty,Npi,Phone,Company
H01,DR001,John,Smith,A,CAR,1234567890,(555) 123-4567,PSN
H01,DR002,Mary,Johnson,,ORTHO,2345678901,555-234-5678,
H02,PHY001,Robert,Williams,T,GENSURG,,,(555) 345-6789,PARSEC`}
              </pre>
            </Box>
          </Box>

          {/* Important Notes */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Important Notes
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Data Processing:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Empty rows are automatically skipped</li>
                <li>Leading and trailing spaces are automatically trimmed</li>
                <li>If Company is not provided, the current company context will be used</li>
                <li>CenterId will be prefixed with the company (e.g., "H01" becomes "PSN-H01")</li>
                <li>Existing physicians with the same Code will be updated</li>
                <li>New physicians will be created if the Code doesn't exist</li>
              </ul>
            </Alert>

            <Alert severity="error">
              <Typography variant="body2">
                <strong>Common Upload Errors:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Missing required headers (CenterId, Code)</li>
                <li>Empty CenterId or Code values</li>
                <li>Missing both FirstName and LastName</li>
                <li>Invalid Specialty values (must match exact spelling)</li>
                <li>Malformed CSV structure (extra commas, unclosed quotes)</li>
              </ul>
            </Alert>
          </Box>

          {/* Upload Process */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Upload Process
            </Typography>
            <Typography variant="body2" color="text.secondary">
              After selecting your CSV file:
            </Typography>
            <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>File is validated for required headers and data format</li>
              <li>Each row is processed and validated</li>
              <li>Progress updates are shown in real-time</li>
              <li>Existing physicians are updated, new ones are created</li>
              <li>Success/error summary is displayed upon completion</li>
            </ol>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhysicianUploadHelpDialog;